'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef } from 'react';

/**
 * Inner component that uses useSearchParams (requires Suspense boundary).
 */
function AnalyticsPageViewInner({ gaId }: { gaId: string }) {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const isFirstRender = useRef(true);

	useEffect(() => {
		// Skip the first render — the initial page_view is already sent by gtag('config', ...)
		if (isFirstRender.current) {
			isFirstRender.current = false;

			return;
		}

		if (typeof window !== 'undefined' && 'gtag' in window) {
			const url = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '');
			// @ts-ignore
			window.gtag('event', 'page_view', {
				page_path: url,
				page_title: document.title,
			});
		}
	}, [pathname, searchParams, gaId]);

	return null;
}

/**
 * Tracks client-side page navigations in Google Analytics.
 * The initial page_view is handled by the gtag('config', ...) in the head.
 * This component handles subsequent soft navigations via Next.js Link.
 *
 * Suspense boundary is inside the component to avoid triggering
 * server-side streaming in the layout (which prevents correct 404 status codes).
 */
export function AnalyticsPageView({ gaId }: { gaId: string }) {
	return (
		<Suspense fallback={null}>
			<AnalyticsPageViewInner gaId={gaId} />
		</Suspense>
	);
}
